import { isOnServer } from '@shared/constants/util'
import { getCookie } from '@/features/shared/utils/cookies'
import { VARIATION_GROUP } from '@shared/constants/server_side_experiments'
import { matchMigratedRoute } from '@shared/constants/appRouterMigration'
import { routeAccessManifest } from '@shared/constants/routeAccessManifest'

// next treats any same origin href as local and soft navigates it, including absolute
// ones like https://www.shipt.com/shop and relative ones, so resolve the same way it
// does rather than pattern matching the raw string. anything off origin, and anything
// unparseable, leaves the app and is not ours to reroute.
const getInAppPathname = (href: string): string | undefined => {
  const { origin, pathname: currentPathname } = window.location
  try {
    const url = new URL(href, origin + currentPathname)
    if (url.origin !== origin) return undefined
    // a query or hash only href stays put, so no router changes hands
    if (url.pathname === currentPathname) return undefined
    return url.pathname
  } catch {
    return undefined
  }
}

// for migrated routes, this reads the same bucket cookie as the proxy so both
// agree on route ownership. it can't see the env kill switch, but that is fine.
// after rollout is disabled, the next request rewrites the bucket cookie to
// control. until then, a mismatch only causes an extra document load of the
// correct page.
const getServingRouter = (href: string): 'app' | 'pages' | undefined => {
  // cookies are per request on the server, and this only guards client navigation.
  if (isOnServer()) return undefined

  const pathname = getInAppPathname(href)
  if (pathname === undefined) return undefined

  const migratedRoute = matchMigratedRoute(pathname)
  if (migratedRoute) {
    return getCookie(migratedRoute.experiment.cookieGroupKey) === VARIATION_GROUP ? 'app' : 'pages'
  }

  // app router pages outside the /shop migration. they aren't bucketed, so the app router
  // owns them unconditionally. the manifest records the src dir each page came from, so
  // this can't drift from what is actually in src/app. resolve the winning entry first and
  // then read its router, since checking app entries alone would ignore a higher precedence
  // pages route that shadows them.
  const isAppRouterRoute = routeAccessManifest.find((r) => r.regex.test(pathname))?.router === 'app'

  // the app router surface is a small closed set, so everything else belongs
  // to the pages router. treating unknown hrefs as app router routes causes an
  // unnecessary RSC request before nextjs falls back to a document load.
  return isAppRouterRoute ? 'app' : 'pages'
}

// app router and pages router routes can't soft navigate between each other, so callers
// hard navigate instead and skip prefetching a payload the destination can't use.
export const getIsCrossRouterNavigation = ({
  href,
  isPages,
}: {
  href: string
  isPages: boolean
}) => {
  const servingRouter = getServingRouter(href)
  if (!servingRouter) return false
  return (servingRouter === 'pages') !== isPages
}
